import React, { Component } from 'react';
import propTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import { Collapse, Input, Label } from 'reactstrap';

import ConfirmationModal from 'app/ConfirmationModal/components/ConfirmationModal';
import sharedStyles from 'app/shared.css';

import { SectionHeader } from 'ui/Components';
import { renderArrowStyle } from 'ui/helpers';

import CheckboxList from './CheckboxList';
import styles from './ContentContext.css';
import * as constants from '../const';

/**
 * Component for Content Context subcomponent
 */
export default class ContentContext extends Component {
  static propTypes = {
    modifyComponent: propTypes.func.isRequired,
    componentDetails: propTypes.object.isRequired,
    isReadonly: propTypes.bool
  };

  static defaultProps = {
    isReadonly: false
  };

  /**
   * constructor to set local state (diplays checkboxes checked/unchecked) from component state
   * @param {object} props - props to this component
   */
  constructor(props) {
    super(props);
    this.state = {
      ...this.getInitialContentContext(),
      showUSGeoTargetingInfo: false,
      showUSLocalMarketsAndStates: false,
      showUSLocalMarketChecklist: false,
      showUSStateChecklist: false,
      showCustomerTargetingInfo: false
    };
    this.handleChange = this.handleChange.bind(this);
    this.areValidDevices = this.areValidDevices.bind(this);
    this.areValidCountryCodes = this.areValidCountryCodes.bind(this);
    this.hideValidationModal = this.hideValidationModal.bind(this);
  }

  /**
   * sets selected content context to the component's state
   * Device and CountryCode content context values gets added and deleted here
   * @param {string} value - selected value
   * @param {string} type - type of the checkbox
   */
  handleChange(value, type) {
    const { modifyComponent } = this.props;
    const { ContentContext } = this.state;

    const contentContext = cloneDeep(ContentContext);
    const { GeolocationLocalMarket, GeolocationState } = contentContext;

    switch (type) {
      case constants.US_LOCAL_MARKET: {
        let usLocalMarkets = GeolocationLocalMarket.filter(lm =>
          constants.ALL_US_LOCAL_MARKETS.includes(lm)
        );
        if (value === constants.SELECT_NONE) {
          // de-select all US local markets
          usLocalMarkets = [];
          contentContext.AllUSLocations = [false];
        } else if (value === constants.SELECT_ALL) {
          // select all US local markets
          usLocalMarkets = constants.ALL_US_LOCAL_MARKETS;
          contentContext.AllUSLocations = [true];
        } else {
          // filter out or add new selected US local market
          usLocalMarkets = usLocalMarkets.includes(value)
            ? usLocalMarkets.filter(usLM => usLM !== value)
            : [...usLocalMarkets, value];
          contentContext.AllUSLocations = [false];
        }
        contentContext.GeolocationLocalMarket = [...usLocalMarkets];
        break;
      }
      case constants.US_STATES: {
        let usStates = GeolocationState.filter(state =>
          constants.ALL_US_STATES.includes(state)
        );
        if (value === constants.SELECT_NONE) {
          // de-select all US states
          usStates = [];
          contentContext.AllUSLocations = [false];
        } else if (value === constants.SELECT_ALL) {
          // select all US states
          usStates = constants.ALL_US_STATES;
          contentContext.AllUSLocations = [contentContext.AllUSLocations.includes(true)];
        } else {
          // filter out or add new selected US state
          usStates = usStates.includes(value)
            ? usStates.filter(states => states !== value)
            : [...usStates, value];
          contentContext.AllUSLocations = [false];
        }
        contentContext.GeolocationState = [...usStates];
        break;
      }
      case constants.DEVICE: {
        const devices = contentContext.Device.includes(value)
          ? contentContext.Device.filter(device => device !== value)
          : [...contentContext.Device, value];
        if (this.areValidDevices(devices)) {
          contentContext.Device = devices;
        } else {
          return;
        }
        break;
      }
      case constants.COUNTRY_CODE: {
        const countryCodes = contentContext.CountryCode.includes(value)
          ? contentContext.CountryCode.filter(country => country !== value)
          : [...contentContext.CountryCode, value];
        if (this.areValidCountryCodes(countryCodes)) {
          contentContext.CountryCode = countryCodes;
        } else {
          return;
        }
        if (value === constants.US_COUNTRY_CODE) {
          const includesUSCountryCode = countryCodes.includes(constants.US_COUNTRY_CODE);
          const usStates = includesUSCountryCode ? constants.ALL_US_STATES : [];
          const usLocalMarkets = includesUSCountryCode ? constants.ALL_US_LOCAL_MARKETS : [];
          contentContext.AllUSLocations = includesUSCountryCode;
          contentContext.GeolocationLocalMarket = [...usLocalMarkets];
          contentContext.GeolocationState = [...usStates];
        }
        break;
      }

      // case for click on indeterminate icon - select all US values
      case constants.US_COUNTRY_CODE: {
        const usStates = value === constants.SELECT_ALL ? constants.ALL_US_STATES : [];
        const usLocalMarkets =
          value === constants.SELECT_ALL ? constants.ALL_US_LOCAL_MARKETS : [];
        contentContext.AllUSLocations = value === constants.SELECT_ALL;
        contentContext.GeolocationLocalMarket = [...usLocalMarkets];
        contentContext.GeolocationState = [...usStates];
        break;
      }
      case constants.CUSTOMER_AUTH_STATE: {
        const customerAuthStateValues = contentContext.CustomerAuthState.includes(value)
          ? contentContext.CustomerAuthState.filter(customerStatus => customerStatus !== value)
          : [...contentContext.CustomerAuthState, value];
        contentContext.CustomerAuthState = customerAuthStateValues;
        break;
      }
      case constants.VISITOR_STATUS: {
        const visitorStatusValues = contentContext.VisitorStatus.includes(value)
          ? contentContext.VisitorStatus.filter(visitorStatus => visitorStatus !== value)
          : [...contentContext.VisitorStatus, value];
        contentContext.VisitorStatus = visitorStatusValues;
        break;
      }
      default:
        break;
    }

    if (
      (contentContext.GeolocationLocalMarket.some(lm =>
        constants.ALL_US_LOCAL_MARKETS.includes(lm)
      ) ||
        contentContext.GeolocationState.some(state =>
          constants.ALL_US_STATES.includes(state)
        )) &&
      !contentContext.CountryCode.includes(constants.US_COUNTRY_CODE)
    ) {
      // if the new content context includes US local markets OR US states, make sure US is included in the Country Code array
      contentContext.CountryCode = [...contentContext.CountryCode, constants.US_COUNTRY_CODE];
    } else if (
      !contentContext.GeolocationLocalMarket.some(lm =>
        constants.ALL_US_LOCAL_MARKETS.includes(lm)
      ) &&
      !contentContext.GeolocationState.some(state =>
        constants.ALL_US_STATES.includes(state)
      ) &&
      contentContext.CountryCode.includes(constants.US_COUNTRY_CODE)
    ) {
      // if the new content context does not include any US local markets nor US states, remove the US Country Code
      contentContext.CountryCode = contentContext.CountryCode.filter(
        country => country !== constants.US_COUNTRY_CODE
      );
    }

    this.setState({ ContentContext: contentContext });
    modifyComponent(contentContext, `${constants.METADATA_FIELDS_LOCATION}.ContentContext`);
  }

  /**
   * handles collapse functionality for "Display Options" sub headers
   * @param {string} value - selected value
   */
  handleCollapse(value) {
    switch (value) {
      case constants.CUSTOMER:
        this.setState(ps => ({
          showCustomerTargetingInfo: !ps.showCustomerTargetingInfo
        }));
        break;
      case constants.US_GEO_TARGETING:
        this.setState(ps => ({
          showUSLocalMarketsAndStates: !ps.showUSLocalMarketsAndStates,
          showUSGeoTargetingInfo: !ps.showUSGeoTargetingInfo
        }));
        break;
      case constants.US_LOCAL_MARKET:
        this.setState(ps => ({
          showUSLocalMarketChecklist: !ps.showUSLocalMarketChecklist
        }));
        break;
      case constants.US_STATES:
        this.setState(ps => ({
          showUSStateChecklist: !ps.showUSStateChecklist
        }));
        break;
      default:
        break;
    }
  }

  /**
   * sets selected component initial content context to the component's state
   * device and countrycodes set to empty array if the contentcontext is not there.
   * adds contentcontext to metadatafields along with existing metadatafields.
   * @returns {object} content context object for initial state
   */
  getInitialContentContext() {
    const { componentDetails } = this.props;
    const metaDataFieldsData = get(componentDetails, constants.METADATA_FIELDS_LOCATION, {});
    const countryCodesData = get(metaDataFieldsData, constants.COUNTRY_CODE_LOCATION, []);
    const customerAuthStateData = get(
      metaDataFieldsData,
      constants.CUSTOMER_AUTH_STATE_LOCATION,
      []
    );
    const visitorStatusData = get(metaDataFieldsData, constants.VISITOR_STATUS_LOCATION, []);
    const deviceData = get(metaDataFieldsData, constants.DEVICE_LOCATION, []);
    const geolocationLocalMarketData = get(
      metaDataFieldsData,
      constants.GEOLOCATION_LOCAL_MARKET_LOCATION,
      []
    );
    const geolocationStateData = get(
      metaDataFieldsData,
      constants.GEOLOCATION_STATE_LOCATION,
      []
    );
    const contentContextData = {
      ContentContext: {
        CountryCode: countryCodesData,
        CustomerAuthState: customerAuthStateData,
        Device: deviceData,
        GeolocationLocalMarket: geolocationLocalMarketData,
        GeolocationState: geolocationStateData,
        VisitorStatus: visitorStatusData
      }
    };
    return { ...contentContextData, failedValidations: [] };
  }

  /**
   * returns true if valid else false and sets faild validation messages in state
   * @param {Array} devices - array of selected devices
   * @returns {boolean} returns true if valid else false
   */
  areValidDevices(devices) {
    if (devices.length === 0) {
      this.setState({
        failedValidations: ['At least one Device is required in Display Options.']
      });
      return false;
    }
    return true;
  }

  /**
   * returns true if valid else false and sets faild validation messages in state
   * @param {object} countryCodes - array of selected country codes
   * @returns {boolean} returns true if valid else false
   */
  areValidCountryCodes(countryCodes) {
    if (countryCodes.length === 0) {
      this.setState({
        failedValidations: ['At least one Country is required in Display Options.']
      });
      return false;
    }
    return true;
  }

  /**
   * hides validation mesage modal on ok click
   */
  hideValidationModal() {
    this.setState({ failedValidations: [] });
  }

  /**
   * renders a OkConfirmationModal
   * @returns {React.Component} modal with ok button
   */
  renderValidationMessage() {
    const { failedValidations } = this.state;

    return (
      <ConfirmationModal
        isOpen={failedValidations.length > 0}
        onOkClick={this.hideValidationModal}
        data-test-id="editorPane-okConfirmationModal"
      >
        <ul className="p-4">
          {failedValidations.map((message, index) => (
            <li key={index}>
              <span className={sharedStyles.validationMessage}>{message}</span>
            </li>
          ))}
        </ul>
      </ConfirmationModal>
    );
  }

  /**
   * Render display options section for content context
   * @returns {Component} matched to input type
   */
  renderContentContext() {
    const { isReadonly } = this.props;
    const {
      showCustomerTargetingInfo,
      showUSLocalMarketsAndStates,
      showUSLocalMarketChecklist,
      showUSStateChecklist,
      showUSGeoTargetingInfo
    } = this.state;
    const deviceSelectedValues = this.state.ContentContext.Device;
    const countrySelectedValues = this.state.ContentContext.CountryCode;
    const customerAuthStateSelectedValues = this.state.ContentContext.CustomerAuthState;
    const visitorStatusSelectedValues = this.state.ContentContext.VisitorStatus;
    const usLocalMarketSelectedValues =
      this.state.ContentContext.GeolocationLocalMarket.filter(lm =>
        constants.ALL_US_LOCAL_MARKETS.includes(lm)
      ) || [];
    const usStatesSelectedValues =
      this.state.ContentContext.GeolocationState.filter(state =>
        constants.ALL_US_STATES.includes(state)
      ) || [];
    const indeterminate = {
      US: false
    };
    if (
      (usLocalMarketSelectedValues.length &&
        usLocalMarketSelectedValues.length !== constants.ALL_US_LOCAL_MARKETS.length) ||
      (usStatesSelectedValues.length &&
        usStatesSelectedValues.length !== constants.ALL_US_STATES.length) ||
      (usStatesSelectedValues.length && !usLocalMarketSelectedValues.length) ||
      (usLocalMarketSelectedValues.length && !usStatesSelectedValues.length)
    ) {
      indeterminate.US = true;
    }
    return (
      <React.Fragment>
        <div className="p-4">
          <CheckboxList
            name="Devices"
            listElements={constants.SUPPORTED_CONTENT_CONTEXT.Device}
            selectedCheckboxes={deviceSelectedValues}
            isReadonly={isReadonly}
            handleChange={this.handleChange}
            handleChangeType={constants.DEVICE}
          />
          <CheckboxList
            name="Country"
            listElements={constants.SUPPORTED_CONTENT_CONTEXT.CountryCode}
            selectedCheckboxes={countrySelectedValues}
            indeterminate={indeterminate}
            isReadonly={isReadonly}
            handleChange={this.handleChange}
            handleChangeType={constants.COUNTRY_CODE}
          />
        </div>
        <SectionHeader
          key="US Geo Targeting"
          onClick={() => this.handleCollapse(constants.US_GEO_TARGETING)}
          level={1}
        >
          US Geo Targeting
          {renderArrowStyle(showUSGeoTargetingInfo)}
        </SectionHeader>
        {showUSLocalMarketsAndStates && (
          <div>
            <SectionHeader
              key="US Local Markets"
              onClick={() => this.handleCollapse(constants.US_LOCAL_MARKET)}
              level={2}
            >
              <div className="d-flex">
                {usLocalMarketSelectedValues.length < constants.ALL_US_LOCAL_MARKETS.length &&
                usLocalMarketSelectedValues.length > 0 ? (
                  <Input
                    id="indeterminate"
                    type="checkbox"
                    className={styles.indeterminate}
                    onChange={() => {
                      return this.handleChange(
                        constants.SELECT_ALL,
                        constants.US_LOCAL_MARKET
                      );
                    }}
                  />
                ) : (
                  <Input
                    id="us-local-markets-check"
                    data-test-id="cc-checkbox-us-local-markets"
                    type="checkbox"
                    className={styles.checkboxMargin}
                    checked={
                      usLocalMarketSelectedValues.length ===
                      constants.ALL_US_LOCAL_MARKETS.length
                    }
                    onChange={() => {
                      return this.handleChange(
                        usLocalMarketSelectedValues.length ===
                          constants.ALL_US_LOCAL_MARKETS.length
                          ? constants.SELECT_NONE
                          : constants.SELECT_ALL,
                        constants.US_LOCAL_MARKET
                      );
                    }}
                  />
                )}
                <Label>Local Markets</Label>
              </div>

              {renderArrowStyle(showUSLocalMarketChecklist)}
            </SectionHeader>
            <Collapse isOpen={showUSLocalMarketChecklist}>
              <CheckboxList
                name={constants.US_LOCAL_MARKET}
                listElements={constants.ALL_US_LOCAL_MARKETS}
                selectedCheckboxes={usLocalMarketSelectedValues}
                customScroll
                isReadonly={isReadonly}
                handleChange={this.handleChange}
                handleChangeType={constants.US_LOCAL_MARKET}
              />
            </Collapse>
            <SectionHeader
              key="States"
              onClick={() => this.handleCollapse(constants.US_STATES)}
              level={2}
            >
              <div className="d-flex">
                {usStatesSelectedValues.length < constants.ALL_US_STATES.length &&
                usStatesSelectedValues.length > 0 ? (
                  <Input
                    id="states-indeterminate"
                    type="checkbox"
                    className={styles.indeterminate}
                    onChange={() => {
                      return this.handleChange(constants.SELECT_ALL, constants.US_STATES);
                    }}
                  />
                ) : (
                  <Input
                    id="us-states-check"
                    data-test-id="cc-checkbox-us-states"
                    type="checkbox"
                    className={styles.checkboxMargin}
                    checked={usStatesSelectedValues.length === constants.ALL_US_STATES.length}
                    onChange={() => {
                      return this.handleChange(
                        usStatesSelectedValues.length === constants.ALL_US_STATES.length
                          ? constants.SELECT_NONE
                          : constants.SELECT_ALL,
                        constants.US_STATES
                      );
                    }}
                  />
                )}
                <Label>States</Label>
              </div>

              {renderArrowStyle(showUSStateChecklist)}
            </SectionHeader>
            <Collapse isOpen={showUSStateChecklist}>
              <CheckboxList
                listElements={constants.ALL_US_STATES}
                selectedCheckboxes={usStatesSelectedValues}
                customScroll
                isReadonly={isReadonly}
                handleChange={this.handleChange}
                handleChangeType={constants.US_STATES}
              />
            </Collapse>
          </div>
        )}
        <div className={styles.whitespace} />
        <SectionHeader
          key="customer"
          onClick={() => this.handleCollapse(constants.CUSTOMER)}
          level={1}
        >
          Customer
          {renderArrowStyle(showCustomerTargetingInfo)}
        </SectionHeader>
        <Collapse isOpen={showCustomerTargetingInfo}>
          <div className="p-4">
            <CheckboxList
              name="Customer Authentication State"
              listElements={constants.SUPPORTED_CONTENT_CONTEXT.CustomerAuthState}
              selectedCheckboxes={customerAuthStateSelectedValues}
              isReadonly={isReadonly}
              handleChange={this.handleChange}
              handleChangeType={constants.CUSTOMER_AUTH_STATE}
            />
            <CheckboxList
              name="Vistor Status"
              listElements={constants.SUPPORTED_CONTENT_CONTEXT.VisitorStatus}
              selectedCheckboxes={visitorStatusSelectedValues}
              isReadonly={isReadonly}
              handleChange={this.handleChange}
              handleChangeType={constants.VISITOR_STATUS}
            />
          </div>
        </Collapse>
      </React.Fragment>
    );
  }

  render() {
    return (
      <div>
        {this.renderContentContext()}
        {this.renderValidationMessage()}
      </div>
    );
  }
}
